  <template>
   <el-card class="box-card" shadow="never" :style="{height:'600px',width:'100%'}">
      <div class="sales-graphic__header">
        <h4 class="sales-graphic__title">{{ title }}</h4>
        <el-checkbox-group v-model="visibleSeries" class="sales-graphic__legend">
          <el-checkbox-button
            v-for="item in seriesControls"
            :key="item.name"
            :label="item.name"
            class="sales-graphic__legend-button"
          >
            <span class="sales-graphic__legend-swatch" :style="{ backgroundColor: item.color }"></span>
            <span>{{ item.label }}</span>
          </el-checkbox-button>
        </el-checkbox-group>
      </div>
      <div id="chart" :style="{height:'500px',width:'100%'}" ></div>
    </el-card>
  </template>
  <script>
  
  import * as echarts from 'echarts';
  import salesAPI from '@/services/API/sales.js'
  import dayjs from 'dayjs'
  import portausUtils from '@/utils/portaus-utils';

  export default {
      name: 'Sales-graphic',
      props: {
            customerId : {
              type : Number,
            },
            filters: {
              type: Object,
            },
            title : {
              type: String,
            },
            showQuantity: {
              type: Boolean,
              default: false,
            },
            months: {
              type: Number,
              default: 12,
            },
            minimumMonths: {
              type: Number,
              default: 12,
            }
          },
        data() {
            return {
              sales: [],
                previousYearSales: [],
                loading : false,
                visibleSeries: [],
                chart: null,
              }
      },
    computed: {
      valuesLabel() {
        return this.$i18n.t('reports.values');
      },
      agencyFeesLabel() {
        return this.$i18n.t('products.agencyFees');
      },
      rebateLabel() {
        return this.$i18n.t('sales_orders.rebate');
      },
      qtyBtLabel() {
        return this.$i18n.t('common.QtyBt');
      },
      qty9LLabel() {
        return this.$i18n.t('common.Qty9L');
      },
      valuesPreviousYearLabel() {
        return `${this.valuesLabel} N-1`;
      },
      agencyFeesPreviousYearLabel() {
        return `${this.agencyFeesLabel} N-1`;
      },
      rebatePreviousYearLabel() {
        return `${this.rebateLabel} N-1`;
      },
      qtyBtPreviousYearLabel() {
        return `${this.qtyBtLabel} N-1`;
      },
      qty9LPreviousYearLabel() {
        return `${this.qty9LLabel} N-1`;
      },
      seriesControls() {
        const controls = [
          { name: this.valuesLabel, label: this.valuesLabel, color: '#3A7BD5' },
          { name: this.agencyFeesLabel, label: this.agencyFeesLabel, color: '#67C23A' },
          { name: this.rebateLabel, label: this.rebateLabel, color: '#E6A23C' },
        ];

        if (this.showQuantity === true) {
          controls.push(
            { name: this.qtyBtLabel, label: this.qtyBtLabel, color: '#909399' },
            { name: this.qty9LLabel, label: this.qty9LLabel, color: '#409EFF' }
          );
        }

        controls.push(
          { name: this.valuesPreviousYearLabel, label: this.valuesPreviousYearLabel, color: '#8DB9FF' },
          { name: this.agencyFeesPreviousYearLabel, label: this.agencyFeesPreviousYearLabel, color: '#9BD98D' },
          { name: this.rebatePreviousYearLabel, label: this.rebatePreviousYearLabel, color: '#F2C36B' }
        );

        if (this.showQuantity === true) {
          controls.push(
            { name: this.qtyBtPreviousYearLabel, label: this.qtyBtPreviousYearLabel, color: '#B1B5BA' },
            { name: this.qty9LPreviousYearLabel, label: this.qty9LPreviousYearLabel, color: '#86C6FF' }
          );
        }

        return controls;
      },
    },
    components : {
    },
    methods : {
      formatAmount(amount) {
            return portausUtils.formatAmount(amount, this.$i18n.locale);
        },
        formatNumber(amount) {
            return portausUtils.formatNumber(amount, this.$i18n.locale);
        },
      resolvePeriodRange() {
        const hasStartDate = _.get(this.filters, 'startDate') != null;
        const hasEndDate = _.get(this.filters, 'endDate') != null;
        const minimumMonths = Math.max(Number(this.minimumMonths || 12), 1);
        const defaultMonths = Math.max(Number(this.months || minimumMonths), 1);

        const endDate = hasEndDate
          ? dayjs(_.get(this.filters, 'endDate')).endOf('month')
          : dayjs().endOf('month');

        let startDate = hasStartDate
          ? dayjs(_.get(this.filters, 'startDate')).startOf('month')
          : endDate.subtract(defaultMonths - 1, 'month').startOf('month');

        if (startDate.isAfter(endDate)) {
          startDate = endDate.startOf('month');
        }

        const minimumStartDate = endDate.subtract(minimumMonths - 1, 'month').startOf('month');
        if (startDate.isAfter(minimumStartDate)) {
          startDate = minimumStartDate;
        }

        const monthKeys = [];
        const xAxis = [];
        let cursor = startDate.startOf('month');
        const endMonth = endDate.startOf('month');

        while (cursor.isBefore(endMonth) || cursor.isSame(endMonth)) {
          monthKeys.push(cursor.format('YYYY-MM'));
          xAxis.push(cursor.format('MMM'));
          cursor = cursor.add(1, 'month');
        }

        return {
          startDate,
          endDate,
          monthKeys,
          xAxis,
        };
      },
      getSalesMonthKey(sale, yearOffset = 0) {
        const txnYear = _.get(sale, 'txn_year');
        const txnMonth = _.get(sale, 'txn_month');

        if (txnYear != null && txnMonth != null) {
          return `${Number(txnYear) + Number(yearOffset)}-${String(txnMonth).padStart(2, '0')}`;
        }

        if (txnMonth != null) {
          const month = String(txnMonth).padStart(2, '0');
          return _.find(this.resolvePeriodRange().monthKeys, k => k.endsWith(`-${month}`));
        }

        return null;
      },
      buildMonthlySeries(salesRows, monthKeys, yearOffset = 0) {
        const monthIndex = {};
        _.forEach(monthKeys, (key, index) => {
          monthIndex[key] = index;
        });

        const agencyFees = new Array(monthKeys.length).fill(0);
        const values = new Array(monthKeys.length).fill(0);
        const discount = new Array(monthKeys.length).fill(0);
        const quantity = new Array(monthKeys.length).fill(0);
        const quantity9L = new Array(monthKeys.length).fill(0);

        _.forEach(salesRows, s => {
          const key = this.getSalesMonthKey(s, yearOffset);
          const index = key != null ? monthIndex[key] : undefined;
          if (index == null) return;

          agencyFees[index] += Math.round(Number(s.agencyfees || 0) * 100) / 100;
          values[index] += Math.round(Number(s.value || 0) * 100) / 100;
          discount[index] += Math.round(Number(s.discount || 0) * 100) / 100;
          quantity[index] += Math.round(Number(s.qty || 0) * 100) / 100;
          quantity9L[index] += Math.round(Number(s.qty9l || 0) * 100) / 100;
        });

        return { agencyFees, values, discount, quantity, quantity9L };
      },
      buildSeriesData(values, formatter, options = {}) {
        return values.map((value) => (value == null ? 0 : value));
      },
      buildComparisonTooltipFormatter() {
        return (params) => {
          const items = Array.isArray(params) ? params : [params];
          const currentYearItems = items.filter(item => !String(item.seriesName || '').includes('N-1'));
          const previousYearItems = items.filter(item => String(item.seriesName || '').includes('N-1'));
          const allItems = [...currentYearItems, ...previousYearItems];

          if (!allItems.length) {
            return '';
          }

          const lines = [`<div style="font-weight:600;margin-bottom:6px;">${allItems[0].axisValueLabel || allItems[0].name || ''}</div>`];

          allItems.forEach(item => {
            const isQuantity = String(item.seriesName || '').includes(this.qtyBtLabel) || String(item.seriesName || '').includes(this.qty9LLabel);
            const formattedValue = isQuantity
              ? this.formatNumber(item.value)
              : this.formatAmount(item.value);

            lines.push(
              `<div style="display:flex;justify-content:space-between;gap:16px;min-width:220px;">` +
                `<span><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${item.color};margin-right:8px;"></span>${item.seriesName}</span>` +
                `<strong>${formattedValue}</strong>` +
              `</div>`
            );
          });

          if (currentYearItems.length && previousYearItems.length) {
            const currentTotal = currentYearItems.reduce((sum, item) => sum + Number(item.value || 0), 0);
            const previousTotal = previousYearItems.reduce((sum, item) => sum + Number(item.value || 0), 0);
            const delta = previousTotal !== 0 ? ((currentTotal - previousTotal) / Math.abs(previousTotal)) * 100 : null;
            const formattedDelta = delta == null ? this.$i18n.t('common.na') : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`;

            lines.push(
              `<div style="border-top:1px solid #ebeef5;margin-top:8px;padding-top:8px;display:flex;justify-content:space-between;gap:16px;">` +
                `<span style="font-weight:600;">Écart global</span>` +
                `<strong>${formattedDelta}</strong>` +
              `</div>`
            );
          }

          return lines.join('');
        };
      },
      buildLegendSelection() {
        const selection = {};

        this.seriesControls.forEach((item) => {
          selection[item.name] = this.visibleSeries.includes(item.name);
        });

        return selection;
      },
      initVisibleSeries() {
        const hiddenByDefault = [
          this.rebateLabel,
          this.rebatePreviousYearLabel,
        ];

        this.visibleSeries = this.seriesControls
          .filter(item => !hiddenByDefault.includes(item.name))
          .map(item => item.name);
      },
      syncLegendSelection() {
        if (!this.chart) {
          return;
        }

        this.chart.setOption({
          legend: {
            selected: this.buildLegendSelection(),
          },
        });
      },
      initChart() {
        const { monthKeys, xAxis } = this.resolvePeriodRange();
        const currentSeries = this.buildMonthlySeries(this.sales, monthKeys, 0);
        const previousSeries = this.buildMonthlySeries(this.previousYearSales, monthKeys, 1);

        if (!this.chart) {
          this.chart = echarts.init(document.getElementById('chart'))
        }
        const series = [
          {
              data: this.buildSeriesData(currentSeries.values),
              type: 'bar',
              stack: 'a',
              name: this.valuesLabel,
              barWidth: 16,
              itemStyle: {
                color: '#3A7BD5',
                borderRadius: [6, 6, 0, 0],
              },
              tooltip: { valueFormatter: (value) => this.formatAmount(value)}
            },
            {
              data: this.buildSeriesData(currentSeries.agencyFees),
              type: 'bar',
              stack: 'b',
              name: this.agencyFeesLabel,
              barWidth: 16,
              itemStyle: {
                color: '#67C23A',
                borderRadius: [6, 6, 0, 0],
              },
              tooltip: { valueFormatter: (value) => this.formatAmount(value)}
            },
            {
              data: this.buildSeriesData(currentSeries.discount),
              type: 'bar',
              stack: 'c',
              name: this.rebateLabel,
              barWidth: 16,
              itemStyle: {
                color: '#E6A23C',
                borderRadius: [6, 6, 0, 0],
              },
              tooltip: { valueFormatter: (value) => this.formatAmount(value)}
            }
            ,
          ];
        if (this.showQuantity === true) {
          series.push({
              data: this.buildSeriesData(currentSeries.quantity),
              type: 'bar',
              stack: 'd',
              name: this.qtyBtLabel,
              barWidth: 16,
              itemStyle: {
                color: '#909399',
                borderRadius: [6, 6, 0, 0],
              },
              tooltip: { valueFormatter: (value) => this.formatNumber(value)}
            },
            {
              data: this.buildSeriesData(currentSeries.quantity9L),
              type: 'bar',
              stack: 'e',
              name: this.qty9LLabel,
              barWidth: 16,
              itemStyle: {
                color: '#409EFF',
                borderRadius: [6, 6, 0, 0],
              },
              tooltip: { valueFormatter: (value) => this.formatNumber(value)}
            });
        }

        const previousYearLineStyle = {
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 7,
          lineStyle: {
            width: 2,
            type: 'dashed',
            opacity: 0.9,
          },
          itemStyle: {
            opacity: 0.95,
          },
          areaStyle: {
            opacity: 0.12,
          },
          z: 10,
          emphasis: {
            focus: 'series',
          },
          tooltip: {
            valueFormatter: (value) => this.formatAmount(value),
          },
        };

        series.push(
          {
            ...previousYearLineStyle,
            data: this.buildSeriesData(previousSeries.values),
            name: this.valuesPreviousYearLabel,
            itemStyle: {
              color: '#8DB9FF',
            },
            lineStyle: {
              width: 2,
              type: 'dashed',
              opacity: 0.8,
            },
          },
          {
            ...previousYearLineStyle,
            data: this.buildSeriesData(previousSeries.agencyFees),
            name: this.agencyFeesPreviousYearLabel,
            itemStyle: {
              color: '#9BD98D',
            },
            lineStyle: {
              width: 2,
              type: 'dashed',
              opacity: 0.8,
            },
          },
          {
            ...previousYearLineStyle,
            data: this.buildSeriesData(previousSeries.discount),
            name: this.rebatePreviousYearLabel,
            itemStyle: {
              color: '#F2C36B',
            },
            lineStyle: {
              width: 2,
              type: 'dashed',
              opacity: 0.8,
            },
          }
        );

        if (this.showQuantity === true) {
          series.push(
            {
              ...previousYearLineStyle,
              data: this.buildSeriesData(previousSeries.quantity),
              name: this.qtyBtPreviousYearLabel,
              itemStyle: {
                color: '#B1B5BA',
              },
              lineStyle: {
                width: 2,
                type: 'dashed',
                opacity: 0.8,
              },
              tooltip: {
                valueFormatter: (value) => this.formatNumber(value),
              },
            },
            {
              ...previousYearLineStyle,
              data: this.buildSeriesData(previousSeries.quantity9L),
              name: this.qty9LPreviousYearLabel,
              itemStyle: {
                color: '#86C6FF',
              },
              lineStyle: {
                width: 2,
                type: 'dashed',
                opacity: 0.8,
              },
              tooltip: {
                valueFormatter: (value) => this.formatNumber(value),
              },
            }
          );
        }

        const stackInfo = {};
        for (let i = 0; i < (series[0] ? series[0].data.length : 0); ++i) {
          for (let j = 0; j < series.length; ++j) {
            const stackName = series[j].stack;
            if (!stackName) {
              continue;
            }
            if (!stackInfo[stackName]) {
              stackInfo[stackName] = {
                stackStart: [],
                stackEnd: []
              };
            }
            const info = stackInfo[stackName];
            const data = series[j].data[i];
            if (data && data !== '-') {
              if (info.stackStart[i] == null) {
                info.stackStart[i] = j;
              }
              info.stackEnd[i] = j;
            }
          }
        }
        for (let i = 0; i < series.length; ++i) {
          if (!series[i].stack) {
            continue;
          }
          const data = series[i].data;
          const info = stackInfo[series[i].stack];
          for (let j = 0; j < series[i].data.length; ++j) {
            // const isStart = info.stackStart[j] === i;
            const isEnd = info.stackEnd[j] === i;
            const topBorder = isEnd ? 20 : 0;
            const bottomBorder = 0;
            data[j] = {
              value: data[j],
              itemStyle: {
                borderRadius: [topBorder, topBorder, bottomBorder, bottomBorder]
              }
            };
          }
        }
        const option = {
          color: ['#3A7BD5', '#67C23A', '#E6A23C', '#909399', '#409EFF', '#8DB9FF', '#9BD98D', '#F2C36B', '#B1B5BA', '#86C6FF'],
          xAxis: {
            type: 'category',
            data: xAxis,
            axisTick: { alignWithLabel: true },
            axisLabel: {
              interval: 0,
            },
          },
          yAxis: {
            type: 'value',
            splitLine: {
              lineStyle: {
                type: 'dashed',
                opacity: 0.35,
              },
            },
            axisLabel: {
              formatter: (value) => this.showQuantity ? this.formatNumber(value) : this.formatAmount(value),
            },
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            },
            confine: true,
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            borderColor: '#ebeef5',
            borderWidth: 1,
            textStyle: {
              color: '#303133',
            },
            formatter: this.buildComparisonTooltipFormatter(),
          },
          legend: {
            show: false,
            selected: this.buildLegendSelection(),
          },
          grid: {
            top: 70,
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
          },
          series: series
        };
        this.chart.setOption(option);
        this.syncLegendSelection();
      },
      fetchSales() {
        this.loading = true;
        var utc = require('dayjs/plugin/utc')
        dayjs.extend(utc)
        const { startDate: resolvedStartDate, endDate: resolvedEndDate } = this.resolvePeriodRange();
        const startDate = resolvedStartDate.utc().format();
        const endDate = resolvedEndDate.utc().format();
        const previousStartDate = resolvedStartDate.subtract(12, 'month').utc().format();
        const previousEndDate = resolvedEndDate.subtract(12, 'month').utc().format();

        const params = {
          customerId: this.customerId != null ? this.customerId : undefined,
          productId: _.get(this.filters, 'productId') != null ? _.get(this.filters, 'productId') : undefined,
          inventoryId: _.get(this.filters, 'inventoryId') != null ? _.get(this.filters, 'inventoryId') : undefined,
          repId: _.get(this.filters, 'repId') != null ? _.get(this.filters, 'repId') : undefined,
          providerId: _.get(this.filters, 'providerId') != null ? _.get(this.filters, 'providerId') : undefined,
          productTypeId: _.get(this.filters, 'productTypeId') != null ? _.get(this.filters, 'productTypeId') : undefined,
          groupBy: 'MONTH',
          startDate,
          endDate,
        }
        const previousParams = {
          ...params,
          startDate: previousStartDate,
          endDate: previousEndDate,
        };
        // Il faut au moins avoir un des deux paramètre suivant...
        if (params.inventoryId != null || params.customerId != null) {
          return Promise.all([
            salesAPI.getSalesEvolution(params),
            salesAPI.getSalesEvolution(previousParams),
          ])
            .then(([sales, previousYearSales]) => {
              this.sales = sales;
              this.previousYearSales = previousYearSales;
              this.loading = false;
            })
        }

        this.sales = [];
        this.previousYearSales = [];
        this.loading = false;
        return Promise.resolve();
        
      },
      refreshChart() {
        return this.fetchSales()
          .then(() => this.initChart());
      },
      disposeChart() {
        if (this.chart) {
          this.chart.dispose();
          this.chart = null;
        }
      }
    },
    watch: {
      filters: {
        handler() {
          this.refreshChart();
        },
        deep: true,
      },
      months() {
        this.refreshChart();
      },
      minimumMonths() {
        this.refreshChart();
      },
      showQuantity() {
        this.initVisibleSeries();
        this.refreshChart();
      },
      visibleSeries() {
        this.syncLegendSelection();
      }
    },
    mounted(){
      this.initVisibleSeries();
      return this.refreshChart();
    },
    beforeDestroy() {
      this.disposeChart();
    }
  }
  </script>
  <style lang="scss" scoped>
  @import '../assets/scss/_variables';

  .sales-graphic__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 8px;
    flex-wrap: wrap;
  }

  .sales-graphic__title {
    margin: 0;
    padding-top: 4px;
  }

  .sales-graphic__legend {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px 12px;
  }

  .sales-graphic__legend-button {
    margin-right: 0;
  }

  .sales-graphic__legend-button ::v-deep .el-checkbox-button__inner {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border-radius: 999px;
    border-left: 1px solid #dcdfe6;
  }

  .sales-graphic__legend-button.is-checked ::v-deep .el-checkbox-button__inner {
    box-shadow: 0 0 0 1px currentColor inset;
  }

  .sales-graphic__legend-swatch {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 6px;
    flex-shrink: 0;
  }
  
  </style>
  